(() => {
  if (window.ShanzhuiAtelier) return;

  const root = window.Shopify?.routes?.root || '/';
  const favoriteKey = 'shanzhui-favorite-products';

  const getFavorites = () => {
    try {
      return JSON.parse(localStorage.getItem(favoriteKey) || '[]');
    } catch (_) {
      return [];
    }
  };

  const setFavorites = (items) => {
    localStorage.setItem(favoriteKey, JSON.stringify([...new Set(items)]));
  };

  const announce = (message, state = 'success') => {
    let toast = document.querySelector('[data-sz-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'sz-toast';
      toast.dataset.szToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.dataset.state = state;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toast.hideTimer);
    toast.hideTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const readCartCount = () => {
    const count = Number.parseInt(document.querySelector('.sz-header__cart-count')?.textContent || '0', 10);
    return Number.isFinite(count) ? count : 0;
  };

  const setCartCount = (value, pulse = true) => {
    const normalizedValue = Math.max(0, Number(value) || 0);
    document.querySelectorAll('.sz-header__cart-count').forEach((count) => {
      count.textContent = normalizedValue;
      count.dataset.cartCount = String(normalizedValue);
      count.toggleAttribute('hidden', normalizedValue === 0);
      count.setAttribute('aria-hidden', normalizedValue === 0 ? 'true' : 'false');
      if (!pulse) return;
      count.classList.remove('is-updated');
      window.requestAnimationFrame(() => count.classList.add('is-updated'));
      window.setTimeout(() => count.classList.remove('is-updated'), 520);
    });
  };

  let cartSyncTimer;
  let cartSyncRequest = 0;

  const updateCartCount = async (pulse = false) => {
    const requestId = ++cartSyncRequest;
    const response = await fetch(`${root}cart.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return null;
    const cart = await response.json();
    if (requestId === cartSyncRequest) setCartCount(cart.item_count, pulse);
    return cart;
  };

  const scheduleCartCountSync = (delay = 180, pulse = true) => {
    window.clearTimeout(cartSyncTimer);
    cartSyncTimer = window.setTimeout(() => updateCartCount(pulse).catch(() => {}), delay);
  };

  const getCartSectionId = () => document.querySelector('#shopify-section-sc-cart-drawer-section')
    ? 'sc-cart-drawer-section'
    : 'cart-drawer-section';

  const ajaxProductFormSelector = 'form.sc-pdp-form, form.sz-product-card__form, form.sz-home-product-card__form';
  const getAddButton = (form) => form?.querySelector('[data-sz-add], .sz-product-card__add, [type="submit"]');

  const enhanceAjaxProductForms = (scope = document) => {
    scope.querySelectorAll(ajaxProductFormSelector).forEach((form) => {
      const button = getAddButton(form);
      if (!button || button.dataset.szAjaxAdd === 'true') return;
      button.dataset.szAjaxAdd = 'true';
      // Keep the Liquid markup as a native submit for the no-JavaScript fallback,
      // then switch to one explicit Ajax action once this script is available.
      button.type = 'button';
    });
  };

  const renderCartDrawer = (html, sectionId = getCartSectionId()) => {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sectionSelector = `#shopify-section-${sectionId}`;
    const nextSection = parsed.querySelector(sectionSelector);
    const currentSection = document.querySelector(sectionSelector);
    const declaredCount = Number(nextSection?.querySelector('[data-sc-cart-count]')?.dataset.scCartCount);
    const nextCount = Number.isFinite(declaredCount)
      ? declaredCount
      : [...(nextSection?.querySelectorAll('input[name="updates[]"]') || [])].reduce((total, input) => total + Math.max(0, Number(input.value) || 0), 0);
    if (nextSection && currentSection) currentSection.replaceWith(nextSection);
    if (nextSection) setCartCount(nextCount, false);
    return nextSection ? nextCount : null;
  };

  const requestCartAdd = async (body, isJson = false, updateDrawer = true) => {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: isJson
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      body
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'This item could not be added.');
    if (updateDrawer) {
      const sectionId = getCartSectionId();
      const renderedCount = data.sections?.[sectionId] ? renderCartDrawer(data.sections[sectionId], sectionId) : null;
      if (renderedCount === null) updateCartCount().catch(() => {});
    }
    return data;
  };

  const addForm = async (form, triggerButton = getAddButton(form)) => {
    const button = triggerButton;
    const label = button?.querySelector('[data-sz-add-label]') || button;
    const original = label?.textContent;
    const quantity = Math.max(1, Number(new FormData(form).get('quantity')) || 1);
    const previousCount = readCartCount();
    setCartCount(previousCount + quantity);
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
      label.textContent = 'Adding…';
    }
    try {
      const formData = new FormData(form);
      formData.append('sections', getCartSectionId());
      formData.append('sections_url', window.location.pathname);
      await requestCartAdd(formData);
      openCartDrawer();
      if (label) label.textContent = 'Added ✓';
      form.querySelector('[data-sz-cart-status]')?.replaceChildren(document.createTextNode('Added to your bag.'));
      announce('Added to your bag.');
    } catch (error) {
      setCartCount(previousCount, false);
      updateCartCount().catch(() => {});
      announce(error.message, 'error');
    } finally {
      if (button) {
        window.setTimeout(() => {
          button.classList.remove('is-loading');
          const currentLabel = label?.textContent?.trim();
          if (currentLabel === 'Adding…' || currentLabel === 'Added ✓') {
            button.disabled = false;
            if (label) label.textContent = original;
          }
        }, 650);
      }
    }
  };

  const openCartDrawer = () => {
    const drawer = document.querySelector('#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    if (drawer instanceof HTMLDialogElement) {
      if (!drawer.open) drawer.showModal();
      return true;
    }
    return false;
  };

  const closeCartDrawer = () => {
    const drawer = document.querySelector('#cart-drawer');
    if (!drawer) return;
    if (typeof drawer.close === 'function') drawer.close();
  };

  const buyNow = async (button) => {
    const form = button.closest('.sc-pdp-buy')?.querySelector('form.sc-pdp-form');
    if (!form) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Opening checkout…';
    try {
      await requestCartAdd(new FormData(form), false, false);
      window.location.assign(button.dataset.checkoutUrl || `${root}checkout`);
    } catch (error) {
      button.disabled = false;
      button.textContent = original;
      announce(error.message, 'error');
    }
  };

  const removeCartLine = async (link) => {
    const sectionId = getCartSectionId();
    link.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: link.dataset.lineKey,
          quantity: 0,
          sections: [sectionId],
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This item could not be removed.');
      renderCartDrawer(data.sections?.[sectionId], sectionId);
      openCartDrawer();
    } catch (error) {
      link.removeAttribute('aria-busy');
      announce(error.message, 'error');
    }
  };

  const syncFavorites = () => {
    const favorites = getFavorites();
    document.querySelectorAll('[data-sz-favorite]').forEach((button) => {
      const active = favorites.includes(button.dataset.productHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active ? 'Saved' : 'Save';
      button.setAttribute('aria-label', `${label} ${button.dataset.productTitle || 'product'}`);
      button.querySelector('[data-sz-favorite-label]')?.replaceChildren(document.createTextNode(label));
    });
  };

  document.addEventListener('click', async (event) => {
    const lightweightDrawer = event.target.closest('[data-sc-cart-drawer]');
    if (lightweightDrawer && event.target === lightweightDrawer) {
      closeCartDrawer();
      return;
    }

    if (event.target.closest('[data-sc-cart-close]')) {
      closeCartDrawer();
      return;
    }

    const removeLink = event.target.closest('[data-sc-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      await removeCartLine(removeLink);
      return;
    }

    if (event.target.closest('#cart-drawer [on\\:click*="remove"], #cart-drawer button[name="remove"], #cart-drawer a[href*="quantity=0"], #cart-drawer [data-cart-remove]')) {
      scheduleCartCountSync(350);
    }

    if (event.target.closest('#cart-drawer [aria-label="Close"], #cart-drawer .theme-drawer__close-button')) {
      scheduleCartCountSync(80, false);
    }

    const cartTrigger = event.target.closest('.sz-header__cart');
    if (cartTrigger && document.querySelector('#cart-drawer')) {
      event.preventDefault();
      openCartDrawer();
      return;
    }

    const favoriteButton = event.target.closest('[data-sz-favorite]');
    if (favoriteButton) {
      event.preventDefault();
      const handle = favoriteButton.dataset.productHandle;
      const favorites = getFavorites();
      const next = favorites.includes(handle) ? favorites.filter((item) => item !== handle) : [...favorites, handle];
      setFavorites(next);
      syncFavorites();
      favoriteButton.classList.remove('is-animating');
      window.requestAnimationFrame(() => favoriteButton.classList.add('is-animating'));
      window.setTimeout(() => favoriteButton.classList.remove('is-animating'), 620);
      return;
    }

    const ajaxAddButton = event.target.closest('[data-sz-ajax-add="true"]');
    if (ajaxAddButton) {
      const form = ajaxAddButton.closest(ajaxProductFormSelector);
      if (!form || ajaxAddButton.disabled) return;
      event.preventDefault();
      await addForm(form, ajaxAddButton);
      return;
    }

    const buyNowButton = event.target.closest('[data-sc-buy-now]');
    if (buyNowButton) {
      event.preventDefault();
      await buyNow(buyNowButton);
      return;
    }

    const addAllButton = event.target.closest('[data-sz-add-all]');
    if (addAllButton) {
      const script = document.querySelector('[data-sz-add-all-data]');
      const items = script ? JSON.parse(script.textContent || '[]') : [];
      if (!items.length) return;
      addAllButton.disabled = true;
      const original = addAllButton.textContent;
      const previousCount = readCartCount();
      const addedCount = items.reduce((total, item) => total + Math.max(1, Number(item.quantity) || 1), 0);
      setCartCount(previousCount + addedCount);
      addAllButton.textContent = 'Adding all…';
      try {
        await requestCartAdd(JSON.stringify({ items, sections: [getCartSectionId()], sections_url: window.location.pathname }), true);
        openCartDrawer();
        announce(`${items.length} products added to your bag.`);
      } catch (error) {
        setCartCount(previousCount, false);
        updateCartCount().catch(() => {});
        announce(error.message, 'error');
      } finally {
        addAllButton.disabled = false;
        addAllButton.textContent = original;
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    if (!event.target.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])')) return;
    const form = event.target.closest(ajaxProductFormSelector);
    if (!form) return;
    const button = getAddButton(form);
    if (!button || button.disabled) return;
    event.preventDefault();
    button.click();
  });

  enhanceAjaxProductForms(document);
  document.addEventListener('DOMContentLoaded', () => {
    enhanceAjaxProductForms(document);
    syncFavorites();
    setCartCount(readCartCount(), false);
  });
  document.addEventListener('shopify:section:load', (event) => {
    enhanceAjaxProductForms(event.target);
    syncFavorites();
  });

  ['cart:update', 'cart:updated', 'cart:change', 'cart:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, () => scheduleCartCountSync());
  });
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (!event.promise) {
      scheduleCartCountSync();
      return;
    }
    event.promise
      .then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount;
        if (Number.isFinite(Number(itemCount))) {
          setCartCount(Number(itemCount));
          return;
        }
        scheduleCartCountSync();
      })
      .catch(() => scheduleCartCountSync());
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleCartCountSync(0, false);
  });
  window.ShanzhuiAtelier = { openCartDrawer, updateCartCount, syncFavorites };
})();
